import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Removed heroicons import
import { orderAPI } from '../services/api';
import toast from 'react-hot-toast';

interface Order {
  id: string;
  status: 'pending' | 'completed' | 'cancelled';
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  items: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    price: number | string;
    total: number;
  }>;
  total_amount: number;
  payment_method: string;
  created_at: string | { seconds: number; nanoseconds: number };
  estimated_delivery_time?: string;
}

const OrderStatusPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      loadOrderStatus();
      // Set up polling for order status updates
      const interval = setInterval(loadOrderStatus, 30000); // Poll every 30 seconds
      return () => clearInterval(interval);
    }
  }, [orderId]);

  const loadOrderStatus = async () => {
    try {
      if (!orderId) return;
      
      const response = await orderAPI.getStatus(orderId);
      console.log('Order status response:', response);
      console.log('Order data:', response.data);
      console.log('Order status:', response.data?.status);
      console.log('Order created_at:', response.data?.created_at);
      setOrder(response.data as Order);
    } catch (error) {
      console.error('Failed to load order status:', error);
      if (loading) {
        toast.error('Failed to load order details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return {
          text: 'Order Placed',
          description: 'Your order has been placed and is awaiting confirmation',
          icon: '🕒',
          color: '#d97706',
          bgColor: '#fef3c7'
        };
      case 'completed':
        return {
          text: 'Out for Delivery',
          description: 'Your order is on the way to you',
          icon: '🚚',
          color: '#f97316',
          bgColor: '#fed7aa'
        };
      case 'cancelled':
        return {
          text: 'Cancelled',
          description: 'Your order has been cancelled',
          icon: '❌',
          color: '#dc2626',
          bgColor: '#fee2e2'
        };
      default:
        return {
          text: 'Unknown Status',
          description: 'Order status unknown',
          icon: '❓',
          color: '#6b7280',
          bgColor: '#f3f4f6'
        };
    }
  };

  const getEstimatedDeliveryTime = () => {
    if (!order) return null;
    
    if (order.estimated_delivery_time) {
      return new Date(order.estimated_delivery_time);
    }
    
    // Calculate estimated time based on order creation (25 minutes)
    const createdTime = formatFirebaseDate(order.created_at);
    return new Date(createdTime.getTime() + 25 * 60 * 1000);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFirebaseDate = (timestamp: string | { seconds: number; nanoseconds: number } | { _seconds: number; _nanoseconds: number }) => {
    let date;
    if (timestamp && typeof timestamp === 'object') {
      if ('seconds' in timestamp) {
        // Firebase timestamp object
        date = new Date(timestamp.seconds * 1000);
      } else if ('_seconds' in timestamp) {
        // Firebase timestamp object with underscore
        date = new Date((timestamp as any)._seconds * 1000);
      } else {
        date = new Date(timestamp as string);
      }
    } else {
      // String or regular Date
      date = new Date(timestamp as string);
    }
    return date;
  };

  if (loading) {
    return (
      <div >
        <div ></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div >
        <div >
          <div >❌</div>
          <h2 >Order Not Found</h2>
          <p >We couldn't find the order you're looking for</p>
          <button
            onClick={() => navigate('/')}
            
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);
  const estimatedDelivery = getEstimatedDeliveryTime();

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', paddingBottom: '120px' }}>
      {/* Header */}
      <header style={{ 
        background: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        position: 'sticky', 
        top: 0, 
        zIndex: 10 
      }}>
        <div style={{ 
          maxWidth: '448px',
          margin: '0 auto',
          padding: '16px', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button 
            onClick={() => navigate('/')} 
            style={{ 
              padding: '8px 16px',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            🏠 Home
          </button>
          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#111827', 
            margin: 0
          }}>Order Status</h1>
          <div style={{ width: '70px' }}></div> {/* Spacer for center alignment */}
        </div>
      </header>

      <div style={{ 
        maxWidth: '448px',
        margin: '0 auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Status Card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: statusConfig.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px'
          }}>
            {statusConfig.icon}
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>{statusConfig.text}</h2>
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            margin: '0 0 16px 0'
          }}>{statusConfig.description}</p>
          
          {estimatedDelivery && order.status !== 'cancelled' && (
            <div style={{
              background: '#f9fafb',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>Estimated Delivery Time</p>
              <p style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: 0
              }}>{formatTime(estimatedDelivery)}</p>
            </div>
          )}
        </div>

        {/* Order Details */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0'
          }}>Order Details</h3>
          
          <div style={{
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '16px',
            marginBottom: '16px'
          }}>
            {order.items?.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                fontSize: '16px'
              }}>
                <span style={{ color: '#374151' }}>{item.product_name} × {item.quantity}</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>₹{(Number(item.price) * item.quantity).toFixed(2)}</span>
              </div>
            )) || <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>No items found</div>}
          </div>
          
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '8px'
            }}>
              <span>Total Amount</span>
              <span>₹{order.total_amount.toFixed(2)}</span>
            </div>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0
            }}>Payment: {order.payment_method.replace('_', ' ')}</p>
          </div>
        </div>

        {/* Delivery Details */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0'
          }}>Delivery Details</h3>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div>
              <p style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>Customer</p>
              <p style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 2px 0'
              }}>{order.customer_name}</p>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>{order.customer_phone}</p>
            </div>
            
            <div>
              <p style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>Delivery Address</p>
              <p style={{
                fontSize: '16px',
                color: '#111827',
                margin: 0
              }}>{order.delivery_address}</p>
            </div>
            
            <div>
              <p style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>Order Time</p>
              <p style={{
                fontSize: '16px',
                color: '#111827',
                margin: 0
              }}>
                {formatFirebaseDate(order.created_at).toLocaleString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div >
          <button
            onClick={() => navigate('/')}
            
          >
            Order Again
          </button>
          
        </div>

        {/* Help */}
        <div >
          <p >Need help with your order?</p>
          <button
            onClick={() => toast('Support will contact you soon', { icon: 'ℹ️' })}
            
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusPage;